import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import * as cognito from '../auth/cognitoClient';
import { AuthError } from '../auth/cognitoClient';
import { decodeJwt, isExpired } from '../auth/jwt';
import { isValidPin } from '../auth/pinMapping';
import { setAuthHandlers } from '../api/client';
import { clearRestorePoint, saveRestorePoint } from '../utils/sessionRestore';
import { clearPosCounter } from '../utils/posCounter';

const AuthContext = createContext(null);

const STORAGE_KEY = 'bhima-store.session';
// The only refresh outcomes that end a session: Cognito itself rejecting the
// refresh token. Network errors, timeouts and throttling are retried instead.
const FATAL_REFRESH_CODES = [
  'NotAuthorizedException',
  'InvalidParameterException',
  'RefreshTokenReuseException',
];
// Guide: refresh once the IdToken is within ~5 min of its 60-min expiry.
const REFRESH_SKEW_SECONDS = 5 * 60;
const REFRESH_CHECK_INTERVAL_MS = 30 * 1000;
export const IDLE_TIMEOUT_MS = 15 * 60 * 1000;
const IDLE_RESET_THROTTLE_MS = 1000;
// Anything the person does that the browser can see. Captured at the window so
// a component calling stopPropagation cannot hide real activity from us.
const ACTIVITY_EVENTS = [
  'mousemove', 'mousedown', 'pointerdown', 'click',
  'keydown', 'keyup', 'input',
  'wheel', 'scroll', 'touchstart', 'touchmove',
];
const ACTIVITY_LISTENER_OPTS = { passive: true, capture: true };

function loadStoredTokens() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function deriveName(email) {
  if (!email) return '';
  const local = email.split('@')[0].replace(/[._]/g, ' ');
  return local.replace(/\b\w/g, (c) => c.toUpperCase());
}

function buildSession(tokens) {
  if (!tokens?.idToken) return null;
  const claims = decodeJwt(tokens.idToken);
  return {
    idToken: tokens.idToken,
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    sub: claims.sub,
    email: claims.email,
    phone: claims.phone_number,
    groups: claims['cognito:groups'] || [],
    name: deriveName(claims.email),
    expiresAt: claims.exp,
  };
}

// The token another tab may have just stored, if it is still good to send.
function currentUsableToken() {
  const stored = loadStoredTokens();
  if (!stored?.idToken) return null;
  try {
    return isExpired(decodeJwt(stored.idToken)) ? null : stored.idToken;
  } catch {
    return null;
  }
}

function needsRefresh(tokens) {
  if (!tokens?.idToken) return false;
  try {
    return isExpired(decodeJwt(tokens.idToken), REFRESH_SKEW_SECONDS);
  } catch {
    return true;
  }
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(() => buildSession(loadStoredTokens()));
  const [initializing, setInitializing] = useState(true);
  const [authNotice, setAuthNotice] = useState(null);

  const refreshInFlightRef = useRef(null);
  const sessionGenRef = useRef(0);
  const sessionRef = useRef(session);
  const idleExpiresAtRef = useRef(null);
  const idleListenersRef = useRef(new Set());
  const idleTimerIdRef = useRef(null);
  const idleLastResetRef = useRef(0);
  const idleResetRef = useRef(null);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const persist = useCallback((tokens) => {
    sessionGenRef.current += 1;
    if (tokens) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    setSession(buildSession(tokens));
  }, []);

  const handleUnauthorized = useCallback(
    (noticeMessage = 'Session timed out. Please sign in again.') => {
      // Involuntary sign-out: keep the screen and anything typed on it so the
      // same user lands back where they were.
      saveRestorePoint(sessionRef.current?.sub);
      setAuthNotice(noticeMessage);
      persist(null);
    },
    [persist]
  );

  const refreshSession = useCallback(() => {
    if (refreshInFlightRef.current) return refreshInFlightRef.current;
    const stored = loadStoredTokens();
    if (!stored?.refreshToken) {
      handleUnauthorized();
      return Promise.resolve(null);
    }
    const startGen = sessionGenRef.current;
    const promise = cognito
      .refreshAuth(stored.refreshToken)
      .then((result) => {
        // Signed out, or another tab rotated the token while we were in flight.
        if (sessionGenRef.current !== startGen) return currentUsableToken();
        const tokens = {
          idToken: result.IdToken,
          accessToken: result.AccessToken,
          // Cognito omits RefreshToken inside its 60s rotation grace window —
          // keep the one we hold rather than storing undefined.
          refreshToken: result.RefreshToken || stored.refreshToken,
        };
        persist(tokens);
        return tokens.idToken;
      })
      .catch((err) => {
        if (sessionGenRef.current !== startGen) return currentUsableToken();
        // Only a token Cognito rejects ends the session. Anything else (offline,
        // DNS, 5xx, throttling) keeps it and is retried on the next check.
        if (err instanceof AuthError && FATAL_REFRESH_CODES.includes(err.code)) {
          handleUnauthorized();
        }
        return null;
      })
      .finally(() => {
        refreshInFlightRef.current = null;
      });
    refreshInFlightRef.current = promise;
    return promise;
  }, [persist, handleUnauthorized]);

  const markActivity = useCallback(() => {
    idleResetRef.current?.();
  }, []);

  useEffect(() => {
    setAuthHandlers({ refresh: refreshSession, onActivity: markActivity });
  }, [refreshSession, markActivity]);

  useEffect(() => {
    const stored = loadStoredTokens();
    if (!stored?.idToken || !needsRefresh(stored)) {
      setInitializing(false);
      return;
    }
    refreshSession().finally(() => setInitializing(false));
  }, [refreshSession]);

  // Sliding session. A wall-clock check beats one long timer: setTimeout is
  // throttled in background tabs and does not run at all while the machine
  // sleeps, so a timer-only refresh can fire long after the token expired.
  useEffect(() => {
    if (!session) return undefined;

    const check = () => {
      const stored = loadStoredTokens();
      if (stored?.refreshToken && needsRefresh(stored)) refreshSession();
    };
    const checkOnForeground = () => {
      if (document.visibilityState === 'visible') check();
    };

    const intervalId = setInterval(check, REFRESH_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', checkOnForeground);
    window.addEventListener('focus', checkOnForeground);
    window.addEventListener('online', check);
    check();

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', checkOnForeground);
      window.removeEventListener('focus', checkOnForeground);
      window.removeEventListener('online', check);
    };
  }, [session, refreshSession]);

  // Another tab refreshed (rotating the shared refresh token) or signed out —
  // adopt its tokens instead of replaying a stale one and getting rejected.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY) return;
      sessionGenRef.current += 1;
      let tokens = null;
      try {
        tokens = e.newValue ? JSON.parse(e.newValue) : null;
      } catch {
        tokens = null;
      }
      setSession(buildSession(tokens));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const notifyIdleListeners = useCallback(() => {
    const expiresAt = idleExpiresAtRef.current;
    idleListenersRef.current.forEach((listener) => listener(expiresAt));
  }, []);

  const clearIdleTracking = useCallback(() => {
    if (idleTimerIdRef.current) {
      clearTimeout(idleTimerIdRef.current);
      idleTimerIdRef.current = null;
    }
    idleResetRef.current = null;
    idleExpiresAtRef.current = null;
    idleLastResetRef.current = 0;
    notifyIdleListeners();
  }, [notifyIdleListeners]);

  const subscribeIdleExpires = useCallback((listener) => {
    idleListenersRef.current.add(listener);
    listener(idleExpiresAtRef.current);
    return () => idleListenersRef.current.delete(listener);
  }, []);

  // Inactivity lock — Ops only. Admins stay signed in for as long as the 30-day
  // rotating refresh token lives, per the auth guide's sliding expiration.
  useEffect(() => {
    const groups = session?.groups || [];
    const idleEnforced = groups.includes('Ops') && !groups.includes('Admin');
    if (!idleEnforced) {
      clearIdleTracking();
      return undefined;
    }

    const resetIdleTimer = () => {
      const now = Date.now();
      if (now - idleLastResetRef.current < IDLE_RESET_THROTTLE_MS && idleTimerIdRef.current) {
        return;
      }
      idleLastResetRef.current = now;

      if (idleTimerIdRef.current) clearTimeout(idleTimerIdRef.current);
      idleExpiresAtRef.current = now + IDLE_TIMEOUT_MS;
      notifyIdleListeners();
      idleTimerIdRef.current = setTimeout(() => {
        handleUnauthorized('Session timed out due to 15 minutes of inactivity.');
      }, IDLE_TIMEOUT_MS);
    };

    // Also fed by writes to the API (see api/client.js), which covers work done
    // with a barcode gun or scale that produces no window-level input events.
    idleResetRef.current = resetIdleTimer;

    const onVisibility = () => {
      if (document.visibilityState === 'visible') resetIdleTimer();
    };

    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, resetIdleTimer, ACTIVITY_LISTENER_OPTS));
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', resetIdleTimer);
    resetIdleTimer();

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, resetIdleTimer, ACTIVITY_LISTENER_OPTS));
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', resetIdleTimer);
      clearIdleTracking();
    };
  }, [session, handleUnauthorized, clearIdleTracking, notifyIdleListeners]);

  const login = useCallback(
    async (identifier, pin) => {
      if (!isValidPin(pin)) {
        throw new AuthError('InvalidPin', 'Enter your 4-digit PIN.');
      }
      const result = await cognito.initiateAuthWithPin(identifier.trim(), pin);
      const tokens = {
        idToken: result.IdToken,
        accessToken: result.AccessToken,
        refreshToken: result.RefreshToken,
      };
      setAuthNotice(null);
      persist(tokens);
      return buildSession(tokens);
    },
    [persist]
  );

  const logout = useCallback(() => {
    if (session?.accessToken) {
      cognito.globalSignOut(session.accessToken);
    }
    // Deliberate sign-out: nothing to come back to.
    clearRestorePoint();
    clearPosCounter();
    setAuthNotice(null);
    persist(null);
  }, [session, persist]);

  const clearAuthNotice = useCallback(() => {
    setAuthNotice(null);
  }, []);

  const signUp = useCallback(({ phone, email, pin }) => cognito.signUp({ phone, email, pin }), []);

  const confirmSignUp = useCallback(
    async (username, code, { phone, pin }) => {
      await cognito.confirmSignUp(username, code);
      return login(phone, pin);
    },
    [login]
  );

  const resendCode = useCallback((username) => cognito.resendConfirmationCode(username), []);

  const forgotPassword = useCallback((email) => cognito.forgotPassword(email), []);

  const confirmForgotPassword = useCallback(
    (email, code, newPin) => cognito.confirmForgotPassword(email, code, newPin),
    []
  );

  const value = useMemo(
    () => ({
      user: session,
      initializing,
      authNotice,
      clearAuthNotice,
      subscribeIdleExpires,
      login,
      logout,
      signUp,
      confirmSignUp,
      resendCode,
      forgotPassword,
      confirmForgotPassword,
    }),
    [
      session,
      initializing,
      authNotice,
      clearAuthNotice,
      subscribeIdleExpires,
      login,
      logout,
      signUp,
      confirmSignUp,
      resendCode,
      forgotPassword,
      confirmForgotPassword,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
